import React, { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { Building2, Users, Activity, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Stats {
  networks: { total: number; active: number; pending: number; suspended: number };
  users: { total: number; active: number };
}

export function SuperAdminOverview() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/v1/super-admin/stats");
        if (res.ok) setStats(await res.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  const cards = stats
    ? [
        {
          label: "Total Tenants",
          value: stats.networks.total,
          icon: Building2,
          color: "bg-blue-50 text-blue-700",
        },
        {
          label: "Active Tenants",
          value: stats.networks.active,
          icon: Activity,
          color: "bg-green-50 text-green-700",
        },
        {
          label: "Pending Invites",
          value: stats.networks.pending,
          icon: AlertCircle,
          color: "bg-amber-50 text-amber-700",
        },
        {
          label: "Total Users",
          value: stats.users.total,
          icon: Users,
          color: "bg-purple-50 text-purple-700",
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Platform Overview</h1>
        <p className="text-gray-500 text-sm mt-1">Cross-tenant metrics and management</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-xl border border-gray-200 p-5"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={cn("p-2 rounded-lg", card.color)}>
                <card.icon className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium text-gray-500">{card.label}</span>
            </div>
            <p className="text-3xl font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      {stats && stats.networks.suspended > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700 font-medium">
            {stats.networks.suspended} suspended tenant{stats.networks.suspended > 1 ? "s" : ""}
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-2">Quick Actions</h2>
        <div className="flex gap-3">
          <Link
            to="/super-admin/tenants"
            className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
          >
            <Building2 className="w-4 h-4" /> Manage Tenants
          </Link>
        </div>
      </div>
    </div>
  );
}
